import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fetchWithCSRF } from '../utils/csrfUtils';
import { getRecipeImageUrl, getS3ImageUrl, getIconUrl } from '../utils/imageUtils';
import RecipeCard from '../components/RecipeCard';
import { Spinner } from 'react-bootstrap';
import styles from './Home.module.css';

const Home = () => {
    const navigate = useNavigate();
    const { pgUser, sessionToken } = useAuth();
    const [loading, setLoading] = useState(true);
    const [thisWeekMealPlan, setThisWeekMealPlan] = useState(null);
    const [nextWeekMealPlan, setNextWeekMealPlan] = useState(null);
    const [favoriteRecipes, setFavoriteRecipes] = useState([]);
    const [exploreRecipes, setExploreRecipes] = useState([]);
    const [pantryIngredients, setPantryIngredients] = useState(null);
    const [selectedFavorite, setSelectedFavorite] = useState(null);
    const [selectedExplore, setSelectedExplore] = useState(null);
    const hiddenFavoriteRefs = useRef({});
    const hiddenExploreRefs = useRef({});

    // Calculate progress for a meal plan
    const calculateProgress = (mealPlan) => {
        if (!mealPlan || !mealPlan.meals) return { cooked: 0, total: 0 };
        const cooked = mealPlan.meals.filter(meal => meal.has_been_cooked).length;
        const total = mealPlan.meals.length;
        return { cooked, total };
    };

    // Handle favorite recipe card click
    useEffect(() => {
        if (selectedFavorite && hiddenFavoriteRefs.current[selectedFavorite]) {
            const cardElement = hiddenFavoriteRefs.current[selectedFavorite];
            // Find the recipe-card div and click it
            const recipeCard = cardElement.querySelector('.recipe-card');
            if (recipeCard) {
                recipeCard.click();
            }
            // Reset after clicking
            setSelectedFavorite(null);
        }
    }, [selectedFavorite]);

    // Handle explore recipe card click
    useEffect(() => {
        if (selectedExplore && hiddenExploreRefs.current[selectedExplore]) {
            const cardElement = hiddenExploreRefs.current[selectedExplore];
            // Find the recipe-card div and click it
            const recipeCard = cardElement.querySelector('.recipe-card');
            if (recipeCard) {
                recipeCard.click();
            }
            // Reset after clicking
            setSelectedExplore(null);
        }
    }, [selectedExplore]);

    // Fetch all data
    useEffect(() => {
        const fetchData = async () => {
            if (!pgUser || !sessionToken) return;

            try {
                setLoading(true);

                // Get today's date in the format YYYY-MM-DD
                const today = new Date();
                const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

                // Fetch this week's meal plan
                const thisWeekResponse = await fetchWithCSRF(
                    `${process.env.REACT_APP_POSTGRES_API_ENDPOINT}/meal-plan/date/${todayStr}/app_user/${pgUser.id}`,
                    {
                        headers: {
                            'Authorization': `Bearer ${sessionToken}`,
                            'Content-Type': 'application/json'
                        }
                    }
                );

                if (thisWeekResponse.ok) {
                    const thisWeekData = await thisWeekResponse.json();
                    if (thisWeekData && thisWeekData[0]) {
                        setThisWeekMealPlan(thisWeekData[0]);

                        // Fetch next week's meal plan
                        const nextWeekResponse = await fetchWithCSRF(
                            `${process.env.REACT_APP_POSTGRES_API_ENDPOINT}/meal-plan/next/${thisWeekData[0].id}/app_user/${pgUser.id}`,
                            {
                                headers: {
                                    'Authorization': `Bearer ${sessionToken}`,
                                    'Content-Type': 'application/json'
                                }
                            }
                        );

                        if (nextWeekResponse.ok) {
                            const nextWeekData = await nextWeekResponse.json();
                            setNextWeekMealPlan(nextWeekData);
                        }
                    }
                }

                // Fetch favorite recipes (limit to 6 for display)
                const favoritesResponse = await fetchWithCSRF(
                    '/api/v1/recipe-favorite/my-favorites/with-ingredients',
                    {
                        headers: {
                            'Authorization': `Bearer ${sessionToken}`
                        }
                    }
                );

                if (favoritesResponse.ok) {
                    const favoritesData = await favoritesResponse.json();
                    if (Array.isArray(favoritesData)) {
                        setFavoriteRecipes(favoritesData.slice(0, 6));
                    }
                }

                // Fetch explore recipes (recipes for the user)
                const exploreResponse = await fetchWithCSRF(
                    `/api/v1/recipe/for-you/${pgUser.id}`,
                    {
                        headers: {
                            'Authorization': `Bearer ${sessionToken}`
                        }
                    }
                );

                if (exploreResponse.ok) {
                    const exploreData = await exploreResponse.json();
                    if (Array.isArray(exploreData)) {
                        setExploreRecipes(exploreData.slice(0, 6));
                    }
                }

                // Fetch pantry ingredients for recipe cards
                if (pgUser?.pantry_id) {
                    const pantryResponse = await fetchWithCSRF(
                        `/api/v1/pantry-ingredient/pantry/${pgUser.pantry_id}/app_user/${pgUser.id}`,
                        {
                            headers: {
                                'Authorization': `Bearer ${sessionToken}`
                            }
                        }
                    );

                    if (pantryResponse.ok) {
                        const pantryData = await pantryResponse.json();
                        if (Array.isArray(pantryData)) {
                            setPantryIngredients(pantryData);
                        }
                    }
                }

            } catch (error) {
                console.error('Error fetching home page data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [pgUser, sessionToken]);

    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <Spinner animation="border" style={{ color: '#52cd92' }}>
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>
        );
    }

    const thisWeekProgress = calculateProgress(thisWeekMealPlan);
    const nextWeekProgress = calculateProgress(nextWeekMealPlan);
    const firstName = pgUser?.first_name || 'there';

    return (
        <div className={styles.container}>
            {/* Header */}
            <div className={styles.header}>
                <h1 className={styles.greeting}>Hi {firstName}! Want to cook?</h1>
            </div>

            {/* Meal Plans Container - This Week and Next Week */}
            <div className={styles.mealPlansWrapper}>
                {/* This Week's Meals */}
                <section className={`${styles.section} ${styles.mealPlanSection}`}>
                    <div 
                        className={styles.sectionHeader}
                        onClick={() => navigate('/meal-plan')}
                        style={{ cursor: 'pointer' }}
                    >
                        <h2 className={styles.sectionTitle}>This Week's Meals</h2>
                        <img 
                            src={getIconUrl('chevron-right.svg')} 
                            alt="View all" 
                            className={styles.chevron}
                        />
                    </div>
                    {thisWeekMealPlan && thisWeekMealPlan.meals && thisWeekMealPlan.meals.length > 0 ? (
                        <>
                            <div className={styles.progressContainer}>
                                <div className={styles.progressBar}>
                                    <div 
                                        className={styles.progressFill}
                                        style={{ 
                                            width: `${(thisWeekProgress.cooked / thisWeekProgress.total) * 100}%` 
                                        }}
                                    />
                                </div>
                                <span className={styles.progressText}>
                                    {thisWeekProgress.cooked} of {thisWeekProgress.total} meals cooked
                                </span>
                            </div>
                            <div className={styles.mealScroll}>
                                {thisWeekMealPlan.meals.map((meal, index) => (
                                    <div 
                                        key={meal.id} 
                                        className={styles.mealCard}
                                        onClick={() => navigate(`/meal-plan?mealId=${meal.id}`)}
                                    >
                                        <div className={styles.mealImageWrapper}>
                                            <img 
                                                src={getRecipeImageUrl(meal.recipe_id)}
                                                alt={meal.recipe_name}
                                                className={styles.mealImage}
                                                onError={(e) => {
                                                    e.target.src = getS3ImageUrl('images/recipeImages/placeholder_recipe.jpg');
                                                }}
                                            />
                                            {meal.has_been_cooked && (
                                                <div className={styles.cookedBadge}>
                                                    <img 
                                                        src={getIconUrl('checkmark-solid.svg')} 
                                                        alt="Cooked" 
                                                        className={styles.checkmark}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className={styles.emptyState}>
                            <p>No meals planned for this week yet.</p>
                            <button 
                                className={styles.primaryButton}
                                onClick={() => navigate('/meal-plan')}
                            >
                                View Meal Plan
                            </button>
                        </div>
                    )}
                </section>

                {/* Next Week's Meals */}
                <section className={`${styles.section} ${styles.mealPlanSection}`}>
                    <div 
                        className={styles.sectionHeader}
                        onClick={() => {
                            if (nextWeekMealPlan?.id) {
                                navigate(`/meal-plan?mealPlanId=${nextWeekMealPlan.id}`);
                            } else {
                                navigate('/meal-plan');
                            }
                        }}
                        style={{ cursor: 'pointer' }}
                    >
                        <h2 className={styles.sectionTitle}>Next Week's Meals</h2>
                        <img 
                            src={getIconUrl('chevron-right.svg')} 
                            alt="View all" 
                            className={styles.chevron}
                        />
                    </div>
                    {nextWeekMealPlan && nextWeekMealPlan.meals && nextWeekMealPlan.meals.length > 0 ? (
                        <>
                            <div className={styles.progressContainer}>
                                <div className={styles.progressBar}>
                                    <div 
                                        className={styles.progressFill}
                                        style={{ 
                                            width: `${(nextWeekProgress.cooked / nextWeekProgress.total) * 100}%` 
                                        }}
                                    />
                                </div>
                                <span className={styles.progressText}>
                                    {nextWeekProgress.cooked} of {nextWeekProgress.total} meals cooked
                                </span>
                            </div>
                            <div className={styles.mealScroll}>
                                {nextWeekMealPlan.meals.map((meal, index) => (
                                    <div 
                                        key={meal.id} 
                                        className={styles.mealCard}
                                        onClick={() => navigate(`/meal-plan?mealPlanId=${nextWeekMealPlan.id}&mealId=${meal.id}`)}
                                    >
                                        <div className={styles.mealImageWrapper}>
                                            <img 
                                                src={getRecipeImageUrl(meal.recipe_id)}
                                                alt={meal.recipe_name}
                                                className={styles.mealImage}
                                                onError={(e) => {
                                                    e.target.src = getS3ImageUrl('images/recipeImages/placeholder_recipe.jpg');
                                                }}
                                            />
                                            {meal.has_been_cooked && (
                                                <div className={styles.cookedBadge}>
                                                    <img 
                                                        src={getIconUrl('checkmark-solid.svg')} 
                                                        alt="Cooked" 
                                                        className={styles.checkmark}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className={styles.emptyState}>
                            <p>Next week's meals will be generated automatically.</p>
                        </div>
                    )}
                </section>
            </div>

            {/* Explore */}
            <section className={styles.section}>
                <div 
                    className={styles.sectionHeader}
                    onClick={() => navigate('/recipes')}
                    style={{ cursor: 'pointer' }}
                >
                    <h2 className={styles.sectionTitle}>Explore Recipes</h2>
                    <img 
                        src={getIconUrl('chevron-right.svg')} 
                        alt="View all" 
                        className={styles.chevron}
                    />
                </div>
                {exploreRecipes.length > 0 ? (
                    <div className={styles.recipeGrid}>
                        {exploreRecipes.map(recipe => (
                            <div 
                                key={recipe.id} 
                                className={styles.recipeImageCard}
                                onClick={() => setSelectedExplore(recipe.id)}
                            >
                                <div className={styles.recipeImageWrapper}>
                                    <img 
                                        src={getRecipeImageUrl(recipe.id)}
                                        alt={recipe.name}
                                        className={styles.recipeImage}
                                        onError={(e) => {
                                            e.target.src = getS3ImageUrl('images/recipeImages/placeholder_recipe.jpg');
                                        }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className={styles.emptyState}>
                        <p>Loading recommendations...</p>
                    </div>
                )}
            </section>

            {/* Your Favorites */}
            <section className={styles.section}>
                <div 
                    className={styles.sectionHeader}
                    onClick={() => navigate('/recipes/saved')}
                    style={{ cursor: 'pointer' }}
                >
                    <h2 className={styles.sectionTitle}>Your Favorites</h2>
                    <img 
                        src={getIconUrl('chevron-right.svg')} 
                        alt="View all" 
                        className={styles.chevron}
                    />
                </div>
                {favoriteRecipes.length > 0 ? (
                    <div className={styles.recipeGrid}>
                        {favoriteRecipes.map(favorite => (
                            <div 
                                key={favorite.recipe_id} 
                                className={styles.recipeImageCard}
                                onClick={() => setSelectedFavorite(favorite.recipe_id)}
                            >
                                <div className={styles.recipeImageWrapper}>
                                    <img 
                                        src={getRecipeImageUrl(favorite.recipe.id)}
                                        alt={favorite.recipe.name}
                                        className={styles.recipeImage}
                                        onError={(e) => {
                                            e.target.src = getS3ImageUrl('images/recipeImages/placeholder_recipe.jpg');
                                        }}
                                    />
                                    <div className={styles.favoriteBadge}>
                                        <img 
                                            src={getIconUrl('filled-heart.svg')} 
                                            alt="Favorite" 
                                            className={styles.heartIcon}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className={styles.emptyState}>
                        <p>You haven't favorited any recipes yet.</p>
                        <button 
                            className={styles.primaryButton}
                            onClick={() => navigate('/recipes')}
                        >
                            Explore Recipes
                        </button>
                    </div>
                )}
            </section>

            

            {/* Hidden RecipeCards for triggering popups */}
            <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
                {favoriteRecipes.map(favorite => (
                    <div 
                        key={`hidden-fav-${favorite.recipe_id}`}
                        ref={el => hiddenFavoriteRefs.current[favorite.recipe_id] = el}
                    >
                        <RecipeCard 
                            recipe={favorite.recipe}
                            isFavorite={true}
                            pantryIngredients={pantryIngredients}
                        />
                    </div>
                ))}
                {exploreRecipes.map(recipe => (
                    <div 
                        key={`hidden-explore-${recipe.id}`}
                        ref={el => hiddenExploreRefs.current[recipe.id] = el}
                    >
                        <RecipeCard 
                            recipe={recipe}
                            isFavorite={false}
                            pantryIngredients={pantryIngredients}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Home;

